import React, { useCallback, useContext, useEffect, useMemo, useState, useRef } from "react";
import * as Sentry from "@sentry/react";
import { observer } from "mobx-react";
import { RootStoreContext } from "src/stores/RootStore";
import { UI } from "@wwimmo/ui";
import { useTranslation } from "react-i18next";
import { Route, selectRoute } from "src/config/routes";
import { LinkContainer } from "react-router-bootstrap";
import { isEmpty } from "lodash";
import { Role } from "src/network/User";
import { useSidebarFunctions } from "src/hooks/sidebar-functions/useSidebarFunctions";
import { sidebarWidthUnit } from "src/stores/UISidebarStore";
import { NetworkConfig } from "src/network/NetworkConfig";
import { Link } from "react-router-dom";
import { SIDEBAR_MENU_PATH } from "src/stores/UISidebarStore";
import { useLocation } from "react-router-dom";
import styles from "./Sidebar.module.css";

export enum CustomerType {
    PUBLIC = 1,
    DEMO = 2,
    TEST = 3
}

const SidebarLogoBase = () => {
    const { uiStore, uiSidebarStore, authStore } = useContext(RootStoreContext);
    const displayLogo =
        authStore.isLoggedIn && (authStore.user?.role === Role.MANAGER || authStore.user?.role === Role.ECADMIN);

    const onLogoLoad = useCallback(
        (e: React.SyntheticEvent<HTMLImageElement>) => {
            const img = e.currentTarget;
            if (!img.naturalWidth || !img.naturalHeight) return;
            // Compute actual rendered width of the image inside its object-fit:contain container.
            const containerW = uiStore.isMobile ? 120 : 140;
            const containerH = uiStore.isMobile ? 45 : 50;
            const naturalAspect = img.naturalWidth / img.naturalHeight;
            const containerAspect = containerW / containerH;
            const renderedW =
                naturalAspect >= containerAspect
                    ? containerW // wide image: width fills container
                    : containerH * naturalAspect; // tall image: height fills container
            uiSidebarStore.setLogoRightEdge(10 + renderedW); // 10px = CSS left offset
        },
        [uiStore.isMobile, uiSidebarStore]
    );

    if (!displayLogo) {
        return null;
    }

    return (
        <div
            id="SidebarLogo"
            style={{
                display:
                    (uiStore.isMobile && uiSidebarStore.sidebarWidth === 0) || !uiSidebarStore.isSidebarLogoDisplayed
                        ? "none"
                        : "block"
            }}
        >
            <Link
                className={`${styles.SidebarLogoImage} ${uiStore.isMobile ? styles.Mobile : ""}`}
                to={
                    authStore.user?.role === Role.ECADMIN && authStore.user?.availablePortalRoles.length === 0
                        ? Route.manager + Route.managerDashboard
                        : selectRoute(Route.managerDashboard, authStore.user?.role)
                }
            >
                <img
                    src={NetworkConfig.brandAssets.logoImage}
                    className={styles.SidebarLogoImageImg}
                    alt="SidebarLogo"
                    onLoad={onLogoLoad}
                />
            </Link>
        </div>
    );
};

const ToolTipBase = (props: any) => {
    const { uiSidebarStore } = useContext(RootStoreContext);

    return <span className={uiSidebarStore.isSidebarCollapsed ? styles.TooltipText : "d-none"}>{props.label}</span>;
};
const ToolTip = observer(ToolTipBase);

const LabelBase = (props: any) => {
    const { uiSidebarStore } = useContext(RootStoreContext);
    return (
        <span className={`ml-2 ${styles.SidebarLinkLabel} ${uiSidebarStore.isSidebarCollapsed ? "d-none" : ""}`}>
            {props.label}
        </span>
    );
};
const Label = observer(LabelBase);

const SidebarOverlayTriggerBase = (props: any) => {
    const renderCollapsedTooltip = useMemo(
        () => (
            <UI.Tooltip id="sidebarTooltip" className={styles.CollapsedTooltipText}>
                {props.label}
            </UI.Tooltip>
        ),
        [props.label]
    );

    return (
        <UI.OverlayTrigger key={"sidebarTooltip2"} placement="right" overlay={renderCollapsedTooltip}>
            <div className={styles.SidebarLinkIcon}>
                <UI.Icon icon={props.icon} className={styles.SidebarLinkIcon} />
            </div>
        </UI.OverlayTrigger>
    );
};
const SidebarOverlayTrigger = observer(SidebarOverlayTriggerBase);

const SidebarMenuPointBase = (props: any) => {
    const { uiSidebarStore, uiStore } = useContext(RootStoreContext);
    const location = useLocation();
    const { currentSidebarMenuPath, setCurrentSidebarMenuBottomPath, setCurrentSidebarMenuPath, isSidebarCollapsed } =
        uiSidebarStore;
    const pathname = location.pathname;

    useEffect(() => {
        const isSettingsPathname =
            pathname === Route.customerusers ||
            pathname === Route.tileAdministration ||
            pathname === Route.easycontactsettings ||
            pathname === Route.ticketingsettings ||
            pathname === Route.communicationSettings ||
            pathname === Route.krediflowConfigurationSettings ||
            pathname === Route.theming ||
            pathname === Route.agendaTemplate ||
            pathname === Route.portalDemo;
        const isServicesPathname =
            pathname === Route.manager + Route.servicesCostsAndInvoicesOverview ||
            pathname === Route.manager + Route.servicesCostsAndInvoicesCurrentMonth ||
            pathname === Route.manager + Route.servicesCostsAndInvoicesHistory ||
            pathname === Route.manager + Route.servicesFeatures ||
            pathname === Route.manager + Route.servicesIntegrations;

        setCurrentSidebarMenuBottomPath(
            isSettingsPathname
                ? SIDEBAR_MENU_PATH.SETTINGS
                : isServicesPathname
                  ? SIDEBAR_MENU_PATH.SERVICES
                  : SIDEBAR_MENU_PATH.NONE
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onClickSidebarMenuPoint = useCallback(
        (menuPath: SIDEBAR_MENU_PATH) => () => {
            if (currentSidebarMenuPath !== menuPath) {
                setCurrentSidebarMenuPath(menuPath);
            }

            const isSubpathOfSettings =
                menuPath.includes(SIDEBAR_MENU_PATH.SETTINGS) && menuPath !== SIDEBAR_MENU_PATH.SETTINGS;

            const isSubpathOfServices =
                menuPath.includes(SIDEBAR_MENU_PATH.SERVICES) && menuPath !== SIDEBAR_MENU_PATH.SERVICES;

            if (!isSubpathOfSettings && !isSubpathOfServices) {
                setCurrentSidebarMenuBottomPath(menuPath);
            }
        },
        [setCurrentSidebarMenuBottomPath, setCurrentSidebarMenuPath, currentSidebarMenuPath]
    );

    return (
        <div
            className={`${styles.SidebarIconTitleContainer} ${styles.TooltipContainer} d-flex align-items-center ${
                uiStore.isMobile ? styles.Mobile : ""
            }`}
            onClick={onClickSidebarMenuPoint(props.menuPath)}
        >
            {isSidebarCollapsed ? (
                <SidebarOverlayTrigger label={props.label} icon={props.icon} />
            ) : (
                <React.Fragment>
                    <UI.Icon icon={props.icon} className={styles.SidebarLinkIcon} />
                    <ToolTip label={props.label} />
                    <Label label={props.label} />
                </React.Fragment>
            )}
        </div>
    );
};

const SidebarNavigationItemBase = (props: any) => {
    const { uiStore, uiSidebarStore } = useContext(RootStoreContext);
    return (
        <LinkContainer
            className={`${props.className} ${props.isCurrentMenuPathActive ? styles.CurrentMenuPath : ""} ${
                uiStore.isMobile && uiSidebarStore.isSidebarCollapsed ? "d-none" : ""
            }`}
            to={props.to}
            onClick={props.onClick}
        >
            <UI.Nav.Link active={false}>
                <SidebarMenuPoint label={props.label} menuPath={props.menuPath} icon={props.icon} />
            </UI.Nav.Link>
        </LinkContainer>
    );
};

const SidebarLinksTopBase = () => {
    const { t } = useTranslation();
    const { uiSidebarStore, authStore, ticketStore, navStore } = useContext(RootStoreContext);
    const sidebarNavigationLinksTop =
        authStore.user?.role === Role.ECADMIN && authStore.user?.availablePortalRoles.length === 0
            ? []
            : [
                  {
                      to: selectRoute(Route.ticketDetails, authStore.user?.role, {
                          ticketnumber: ticketStore.NEW_TICKET_ID
                      }),
                      label: t("screens.tickets.new_ticket"),
                      icon: UI.SVGIcon.Plus,
                      menuPath: SIDEBAR_MENU_PATH.NEW_TICKET,
                      isDisplayed: navStore.hasFeatureTicketing,
                      backgroundColorDarker: true
                  },
                  {
                      to: selectRoute(Route.managerDashboard, authStore.user?.role),
                      label: t("labels.dashboard"),
                      icon: UI.SVGIcon.DashboardSidebar,
                      menuPath: SIDEBAR_MENU_PATH.MANAGERDASHBOARD,
                      isDisplayed: true
                  },
                  {
                      to: selectRoute(Route.tickets, authStore.user?.role),
                      label: t("screens.tickets.sidebar_title"),
                      icon: UI.SVGIcon.ListView,
                      menuPath: SIDEBAR_MENU_PATH.TICKETS,
                      isDisplayed: navStore.hasFeatureTicketing
                  },
                  {
                      to: selectRoute(Route.creditors, authStore.user?.role),
                      label: t("screens.kredi_flow.sidebar_title"),
                      icon: UI.SVGIcon.CredIcon,
                      menuPath: SIDEBAR_MENU_PATH.KREDI_FLOW,
                      isDisplayed: navStore.hasFeatureKrediFlow
                  },
                  {
                      to: selectRoute(Route.owners, authStore.user?.role),
                      label: t("screens.owners.title"),
                      icon: UI.SVGIcon.Owners,
                      menuPath: SIDEBAR_MENU_PATH.OWNERS,
                      isDisplayed: true
                  },
                  {
                      to: selectRoute(Route.realestates, authStore.user?.role),
                      label: t("screens.realestates.title"),
                      icon: UI.SVGIcon.Realestates,
                      menuPath: SIDEBAR_MENU_PATH.REALESTATES,
                      isDisplayed: true
                  },
                  {
                      to: selectRoute(Route.assemblies, authStore.user?.role),
                      label: t("screens.assemblies.sidebar_title"),
                      icon: UI.SVGIcon.Assembly,
                      menuPath: SIDEBAR_MENU_PATH.ASSEMBLIES,
                      isDisplayed: navStore.hasFeatureCondominiumownerportal
                  }
              ];

    return (
        <div className="SidebarLinksTop mb-auto">
            <UI.Nav className={styles.SidebarNavigation}>
                {sidebarNavigationLinksTop
                    .filter((navLink) => navLink.isDisplayed)
                    .map((navLink, index) => {
                        return (
                            <SidebarNavigationItem
                                key={`item-${navLink.menuPath}`}
                                label={navLink.label}
                                to={navLink.to}
                                icon={navLink.icon}
                                className={`${styles.SidebarNavigationItem} ${
                                    navLink.backgroundColorDarker ? styles.BackgroundColorDarker : ""
                                }`}
                                menuPath={navLink.menuPath}
                                isCurrentMenuPathActive={navLink.menuPath === uiSidebarStore.currentSidebarMenuPath}
                            />
                        );
                    })}
            </UI.Nav>
        </div>
    );
};

const SidebarLinksBottomBase = () => {
    const { t } = useTranslation();
    const { uiStore, uiSidebarStore, authStore, navStore } = useContext(RootStoreContext);
    const { collapseSidebar, expandSidebar } = useSidebarFunctions();

    const displayMenuPointSettings =
        authStore.user?.availableRoles &&
        (authStore.user.availableRoles.includes(Role.ADMINISTRATOR) ||
            authStore.user.availableRoles.includes(Role.ECADMIN));
    const displayMenuPointServices =
        authStore.user?.availableRoles && authStore.user.availableRoles.includes(Role.ADMINISTRATOR);
    const accordionActiveKey =
        authStore.user?.role === Role.ECADMIN && authStore.user?.availablePortalRoles.length === 0
            ? SIDEBAR_MENU_PATH.SETTINGS
            : uiSidebarStore.currentSidebarMenuBottomPath;

    const sidebarNavigationLinksSettings: {
        to: string;
        label: string;
        icon: (props: React.SVGProps<SVGSVGElement>) => JSX.Element;
        menuPath: SIDEBAR_MENU_PATH;
    }[] = [];

    if (displayMenuPointSettings) {
        if (authStore.user?.availableRoles.includes(Role.ADMINISTRATOR)) {
            sidebarNavigationLinksSettings.push({
                label: t("screens.account.manage_users"),
                to: Route.customerusers,
                icon: UI.SVGIcon.ProfileDashboard,
                menuPath: SIDEBAR_MENU_PATH.SETTINGS_CUSTOMERUSERS
            });
        }

        if (
            authStore.user?.availableRoles.includes(Role.ADMINISTRATOR) ||
            authStore.user?.availableRoles.includes(Role.ECADMIN)
        ) {
            sidebarNavigationLinksSettings.push({
                label: t("sidebar.settings.design"),
                to: Route.theming,
                icon: UI.SVGIcon.Brush,
                menuPath: SIDEBAR_MENU_PATH.SETTINGS_THEMING
            });
        }

        if (authStore.user?.availableRoles.includes(Role.ADMINISTRATOR)) {
            sidebarNavigationLinksSettings.push({
                label: `${t("sidebar.settings.realestate_dashboard_1")}${uiStore.isMobile ? "" : "-"}${t(
                    "sidebar.settings.realestate_dashboard_2"
                )}`,
                to: Route.tileAdministration,
                icon: UI.SVGIcon.DashboardSidebar,
                menuPath: SIDEBAR_MENU_PATH.SETTINGS_TILE_ADMINISTRATION
            });
        }

        if (authStore.user?.availableRoles.includes(Role.ECADMIN)) {
            sidebarNavigationLinksSettings.push({
                label: t("sidebar.settings.easycontact"),
                to: Route.easycontactsettings,
                icon: UI.SVGIcon.EasyContact,
                menuPath: SIDEBAR_MENU_PATH.SETTINGS_EASYCONTACT
            });
        }

        if (authStore.user?.availableRoles.includes(Role.ADMINISTRATOR)) {
            sidebarNavigationLinksSettings.push({
                label: t("sidebar.settings.agenda_template"),
                to: Route.agendaTemplate,
                icon: UI.SVGIcon.SortedList,
                menuPath: SIDEBAR_MENU_PATH.SETTINGS_AGENDATEMPLATE
            });
        }

        if (authStore.user?.availableRoles.includes(Role.ADMINISTRATOR)) {
            sidebarNavigationLinksSettings.push({
                label: t("sidebar.settings.portal_demo"),
                to: Route.portalDemo,
                icon: UI.SVGIcon.Computer,
                menuPath: SIDEBAR_MENU_PATH.SETTINGS_PORTAL_DEMO
            });
        }

        if (authStore.user?.availableRoles.includes(Role.ADMINISTRATOR)) {
            sidebarNavigationLinksSettings.push({
                label: t("sidebar.settings.userratingsportal"),
                to: Route.userRatingsPortal,
                icon: UI.SVGIcon.UserFeedback,
                menuPath: SIDEBAR_MENU_PATH.SETTINGS_USER_FEEDBACK
            });
        }

        if (authStore.user?.availableRoles.includes(Role.ADMINISTRATOR) && navStore.hasFeatureTicketing) {
            sidebarNavigationLinksSettings.push({
                label: t("sidebar.settings.ticketing"),
                to: Route.ticketingsettings,
                icon: UI.SVGIcon.ListView,
                menuPath: SIDEBAR_MENU_PATH.SETTINGS_TICKETING
            });
        }

        if (authStore.user?.availableRoles.includes(Role.ADMINISTRATOR) && navStore.hasFeatureKrediFlow) {
            sidebarNavigationLinksSettings.push({
                label: t("screens.kredi_flow.title"),
                to: Route.krediflowConfigurationSettings,
                icon: UI.SVGIcon.CredIcon,
                menuPath: SIDEBAR_MENU_PATH.SETTINGS_KREDI_FLOW
            });
        }
    }

    sidebarNavigationLinksSettings.push({
        label: t("screens.communication_settings.sidebar_title"),
        to: Route.communicationSettings,
        icon: UI.SVGIcon.Mail,
        menuPath: SIDEBAR_MENU_PATH.SETTINGS_COMMUNICATION
    });

    const sidebarNavigationLinksServices = authStore.user?.hasOnlyEcAdminRole
        ? []
        : [
              {
                  to: selectRoute(Route.servicesFeatures, authStore.user?.role),
                  label: t("sidebar.services.features_and_integrations"),
                  icon: UI.SVGIcon.Switcher,
                  menuPath: SIDEBAR_MENU_PATH.SERVICES_FEATURE_AND_INTEGRATION
              },
              {
                  to: selectRoute(Route.servicesCostsAndInvoicesOverview, authStore.user?.role),
                  label: t("sidebar.services.costs_and_invoices"),
                  icon: UI.SVGIcon.FinancesSidebar,
                  menuPath: SIDEBAR_MENU_PATH.SERVICES_COSTS_AND_INVOICES
              }
          ];

    const onToggleSidebar = useCallback(() => {
        if (uiSidebarStore.isSidebarCollapsed) {
            expandSidebar();
        } else {
            collapseSidebar();
        }
    }, [collapseSidebar, expandSidebar, uiSidebarStore.isSidebarCollapsed]);

    return (
        <div className="SidebarLinksBottom">
            <UI.Accordion defaultActiveKey={accordionActiveKey} activeKey={accordionActiveKey} flush>
                {displayMenuPointServices ? (
                    <UI.Accordion.Item eventKey={SIDEBAR_MENU_PATH.SERVICES}>
                        <UI.Accordion.Header className={styles.AccordionHeader}>
                            <SidebarMenuPoint
                                label={t("sidebar.services.title")}
                                menuPath={SIDEBAR_MENU_PATH.SERVICES}
                                icon={UI.SVGIcon.Puzzle}
                            />
                        </UI.Accordion.Header>
                        <UI.Accordion.Body>
                            <UI.Nav className={styles.SidebarSubNavigation}>
                                {sidebarNavigationLinksServices.map((navLink, index) => (
                                    <SidebarNavigationItem
                                        key={`item-${navLink.menuPath}`}
                                        label={navLink.label}
                                        to={navLink.to}
                                        icon={navLink.icon}
                                        className={styles.SidebarSubNavigationItem}
                                        menuPath={navLink.menuPath}
                                        isCurrentMenuPathActive={
                                            navLink.menuPath === uiSidebarStore.currentSidebarMenuPath
                                        }
                                    />
                                ))}
                            </UI.Nav>
                        </UI.Accordion.Body>
                    </UI.Accordion.Item>
                ) : undefined}
                {sidebarNavigationLinksSettings.length > 0 ? (
                    <UI.Accordion.Item eventKey={SIDEBAR_MENU_PATH.SETTINGS}>
                        <UI.Accordion.Header className={styles.AccordionHeader}>
                            <SidebarMenuPoint
                                label={t("sidebar.settings.title")}
                                menuPath={SIDEBAR_MENU_PATH.SETTINGS}
                                icon={UI.SVGIcon.Cogwheel}
                            />
                        </UI.Accordion.Header>
                        <UI.Accordion.Body>
                            <UI.Nav className={styles.SidebarSubNavigation}>
                                {sidebarNavigationLinksSettings.map((navLink, index) => (
                                    <SidebarNavigationItem
                                        key={`item-${navLink.menuPath}`}
                                        label={navLink.label}
                                        to={navLink.to}
                                        icon={navLink.icon}
                                        className={styles.SidebarSubNavigationItem}
                                        menuPath={navLink.menuPath}
                                        isCurrentMenuPathActive={
                                            navLink.menuPath === uiSidebarStore.currentSidebarMenuPath
                                        }
                                    />
                                ))}
                            </UI.Nav>
                        </UI.Accordion.Body>
                    </UI.Accordion.Item>
                ) : undefined}
            </UI.Accordion>
            <div className={styles.SidebarCollapseIcon} onClick={onToggleSidebar}>
                <UI.Icon icon={uiSidebarStore.isSidebarCollapsed ? UI.SVGIcon.ChevronRight : UI.SVGIcon.ChevronLeft} />
            </div>
        </div>
    );
};

const SidebarLogo = observer(SidebarLogoBase);
const SidebarMenuPoint = observer(SidebarMenuPointBase);
const SidebarNavigationItem = observer(SidebarNavigationItemBase);
const SidebarLinksTop = observer(SidebarLinksTopBase);
const SidebarLinksBottom = observer(SidebarLinksBottomBase);

const SidebarBase = () => {
    const { uiStore, uiSidebarStore, authStore, navStore } = useContext(RootStoreContext);
    const location = useLocation();
    const [hasInitialized, setHasInitialized] = useState<boolean>(false);
    const { upsertUsersettingSidebar } = useSidebarFunctions();

    useEffect(() => {
        const hasUserBeenSet = !isEmpty(navStore.user);
        if (
            !hasInitialized &&
            hasUserBeenSet &&
            (authStore.user?.role === Role.MANAGER ||
                (authStore.user?.role === Role.ECADMIN && authStore.user?.availablePortalRoles.length === 0))
        ) {
            const isSidebarCollapsedUserSetting = navStore.user.isSidebarCollapsedUserSetting;

            uiSidebarStore.setIsSidebarActive(true);

            // Initial Setting of UMS.UserSetting - "PORTAL.IS_SIDEBAR_COLLAPSED"
            if (!isSidebarCollapsedUserSetting) {
                upsertUsersettingSidebar(false);
                uiSidebarStore.expandSidebar();
            } else if (isSidebarCollapsedUserSetting.value === "true") {
                uiSidebarStore.collapseSidebar();
            } else if (isSidebarCollapsedUserSetting.value === "false") {
                uiSidebarStore.expandSidebar();
            }
        }

        return () => {
            setHasInitialized(false);
        };
    }, [
        hasInitialized,
        navStore.user.isSidebarCollapsedUserSetting,
        uiSidebarStore,
        authStore.user?.role,
        navStore.user,
        upsertUsersettingSidebar,
        authStore.user?.availablePortalRoles.length
    ]);

    useEffect(() => {
        // Initial Setting of active menu path
        if (location.pathname === Route.manager + Route.owners) {
            uiSidebarStore.setCurrentSidebarMenuPath(SIDEBAR_MENU_PATH.OWNERS);
        } else if (location.pathname === Route.manager + Route.realestates) {
            uiSidebarStore.setCurrentSidebarMenuPath(SIDEBAR_MENU_PATH.REALESTATES);
        } else if (
            location.pathname === Route.manager + Route.assemblies ||
            location.pathname === Route.manager + Route.assembly
        ) {
            uiSidebarStore.setCurrentSidebarMenuPath(SIDEBAR_MENU_PATH.ASSEMBLIES);
        } else if (
            location.pathname === Route.manager + Route.tickets ||
            location.pathname === Route.manager + Route.ticket ||
            location.pathname === Route.manager + Route.ticketDetails
        ) {
            uiSidebarStore.setCurrentSidebarMenuPath(SIDEBAR_MENU_PATH.TICKETS);
        } else if (location.pathname === Route.manager + Route.managerDashboard) {
            uiSidebarStore.setCurrentSidebarMenuPath(SIDEBAR_MENU_PATH.MANAGERDASHBOARD);
        } else if (location.pathname === Route.customerusers) {
            uiSidebarStore.setCurrentSidebarMenuPath(SIDEBAR_MENU_PATH.SETTINGS_CUSTOMERUSERS);
        } else if (location.pathname === Route.tileAdministration) {
            uiSidebarStore.setCurrentSidebarMenuPath(SIDEBAR_MENU_PATH.SETTINGS_TILE_ADMINISTRATION);
        } else if (location.pathname === Route.easycontactsettings) {
            uiSidebarStore.setCurrentSidebarMenuPath(SIDEBAR_MENU_PATH.SETTINGS_EASYCONTACT);
        } else if (location.pathname === Route.ticketingsettings) {
            uiSidebarStore.setCurrentSidebarMenuPath(SIDEBAR_MENU_PATH.SETTINGS_TICKETING);
        } else if (location.pathname === Route.communicationSettings) {
            uiSidebarStore.setCurrentSidebarMenuPath(SIDEBAR_MENU_PATH.SETTINGS_COMMUNICATION);
        } else if (location.pathname === Route.krediflowConfigurationSettings) {
            uiSidebarStore.setCurrentSidebarMenuPath(SIDEBAR_MENU_PATH.SETTINGS_KREDI_FLOW);
        } else if (location.pathname === Route.theming) {
            uiSidebarStore.setCurrentSidebarMenuPath(SIDEBAR_MENU_PATH.SETTINGS_THEMING);
        } else if (location.pathname === Route.agendaTemplate) {
            uiSidebarStore.setCurrentSidebarMenuPath(SIDEBAR_MENU_PATH.SETTINGS_AGENDATEMPLATE);
        } else if (location.pathname === Route.portalDemo) {
            uiSidebarStore.setCurrentSidebarMenuPath(SIDEBAR_MENU_PATH.SETTINGS_PORTAL_DEMO);
        } else if (
            location.pathname === Route.manager + Route.servicesCostsAndInvoicesOverview ||
            location.pathname === Route.manager + Route.servicesCostsAndInvoicesCurrentMonth ||
            location.pathname === Route.manager + Route.servicesCostsAndInvoicesHistory
        ) {
            uiSidebarStore.setCurrentSidebarMenuPath(SIDEBAR_MENU_PATH.SERVICES_COSTS_AND_INVOICES);
        } else if (
            location.pathname === Route.manager + Route.servicesFeatures ||
            location.pathname === Route.manager + Route.servicesIntegrations
        ) {
            uiSidebarStore.setCurrentSidebarMenuPath(SIDEBAR_MENU_PATH.SERVICES_FEATURE_AND_INTEGRATION);
        }

        const isSettingsPathname =
            location.pathname === Route.customerusers ||
            location.pathname === Route.tileAdministration ||
            location.pathname === Route.easycontactsettings ||
            location.pathname === Route.ticketingsettings ||
            location.pathname === Route.communicationSettings ||
            location.pathname === Route.krediflowConfigurationSettings ||
            location.pathname === Route.theming ||
            location.pathname === Route.agendaTemplate ||
            location.pathname === Route.portalDemo;
        const isServicesPathname =
            location.pathname === Route.manager + Route.servicesCostsAndInvoicesOverview ||
            location.pathname === Route.manager + Route.servicesCostsAndInvoicesCurrentMonth ||
            location.pathname === Route.manager + Route.servicesCostsAndInvoicesHistory ||
            location.pathname === Route.manager + Route.servicesFeatures ||
            location.pathname === Route.manager + Route.servicesIntegrations;

        uiSidebarStore.setCurrentSidebarMenuBottomPath(
            isSettingsPathname
                ? SIDEBAR_MENU_PATH.SETTINGS
                : isServicesPathname
                  ? SIDEBAR_MENU_PATH.SERVICES
                  : SIDEBAR_MENU_PATH.NONE
        );
        // Only run one time on initial load
        // eslint-disable-next-line
    }, [location.pathname, uiStore]);

    const sidebarRef = useRef<HTMLDivElement>(null);
    const [sidebarOverflow, setSidebarOverflow] = useState(false);

    const checkOverflow = () => {
        if (sidebarRef.current) {
            const heightOfSidebarLink = 52;
            const isOverflowing =
                sidebarRef.current.scrollHeight + heightOfSidebarLink > sidebarRef.current.clientHeight;
            setSidebarOverflow(isOverflowing);
        }
    };

    useEffect(() => {
        const currentSidebarRef = sidebarRef.current;
        checkOverflow();

        const resizeObserver = new ResizeObserver(checkOverflow);

        if (currentSidebarRef) {
            resizeObserver.observe(currentSidebarRef);
        }

        return () => {
            if (currentSidebarRef) {
                resizeObserver.unobserve(currentSidebarRef);
            }
        };
    }, []);

    const sidebarContainerStyle = {
        width: uiStore.isMobile
            ? `${uiSidebarStore.sidebarWidth}${sidebarWidthUnit.mobile}`
            : `${uiSidebarStore.sidebarWidth}${sidebarWidthUnit.desktop}`,
        overflow: uiSidebarStore.sidebarWidth === 0 ? "hidden" : "visible"
    };
    const sidebarLinksStyle = {
        marginTop: uiStore.isMobile
            ? `${uiStore.getHeaderHeight()}px`
            : `${uiStore.getHeaderHeight() + uiStore.navigationBarHeight}px`
    };
    const sidebarLinksClassName =
        sidebarOverflow && !uiSidebarStore.isSidebarCollapsed
            ? `${styles.SidebarLinks} d-flex flex-column h-100`
            : sidebarOverflow && uiSidebarStore.isSidebarCollapsed
              ? `${styles.SidebarLinksCollapsed} d-flex flex-column h-100`
              : "d-flex flex-column h-100";

    return (
        <>
            <SidebarLogo />
            <div
                id="SidebarContainer"
                className={`${styles.Sidebar} d-flex flex-column ${uiStore.isMobile ? styles.Mobile : ""}`}
                style={sidebarContainerStyle}
            >
                <div id="SidebarLinks" ref={sidebarRef} className={sidebarLinksClassName} style={sidebarLinksStyle}>
                    <SidebarLinksTop />
                    <SidebarLinksBottom />
                </div>
            </div>
        </>
    );
};

export const Sidebar = Sentry.withProfiler(observer(SidebarBase));
